
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Play, Clock, CheckCircle, XCircle, TestTube, Activity } from 'lucide-react';
import type { CustomAction } from '@/types/action';
import { TestExecutionRunner } from './components/TestExecutionRunner';

interface ActionTestingTabProps {
  action: CustomAction;
}

export const ActionTestingTab = ({ action }: ActionTestingTabProps) => {
  const tests = action.tests || [];
  const hasTests = tests.length > 0;
  const lastTest = hasTests ? tests[tests.length - 1] : null;
  const successfulTests = tests.filter(t => t.success).length;
  const failedTests = tests.filter(t => !t.success).length;

  const handleTestComplete = (result: any) => {
    console.log('Test completed:', result);
    // Here you would typically save the test result to the action's test history
  };

  return (
    <div className="space-y-6">
      {/* Test Execution Runner */}
      <TestExecutionRunner 
        action={action} 
        onTestComplete={handleTestComplete}
      />

      {/* Test Statistics */}
      {hasTests && (
        <Card className="border-0 shadow-sm bg-gradient-to-r from-gray-50/50 to-slate-50/50 backdrop-blur-sm">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg font-semibold">
              <Activity className="h-5 w-5 text-blue-600" />
              Test Statistics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-white/60 rounded-lg border border-white/60 backdrop-blur-sm">
                <div className="text-2xl font-bold text-gray-700">{tests.length}</div>
                <div className="text-sm text-gray-600">Total Tests</div>
              </div>
              <div className="text-center p-4 bg-green-50/60 rounded-lg border border-green-200/60 backdrop-blur-sm">
                <div className="text-2xl font-bold text-green-700">{successfulTests}</div>
                <div className="text-sm text-green-600">Successful</div>
              </div>
              <div className="text-center p-4 bg-red-50/60 rounded-lg border border-red-200/60 backdrop-blur-sm">
                <div className="text-2xl font-bold text-red-700">{failedTests}</div>
                <div className="text-sm text-red-600">Failed</div>
              </div>
              <div className="text-center p-4 bg-blue-50/60 rounded-lg border border-blue-200/60 backdrop-blur-sm">
                <div className="text-2xl font-bold text-blue-700">
                  {tests.length > 0 ? Math.round((successfulTests / tests.length) * 100) : 0}%
                </div>
                <div className="text-sm text-blue-600">Success Rate</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Test History */}
      <Card className="border-0 shadow-sm bg-gradient-to-r from-gray-50/50 to-slate-50/50 backdrop-blur-sm">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg font-semibold">
            <Clock className="h-5 w-5 text-blue-600" />
            Test History
            <Badge variant="outline" className="ml-2 text-xs">
              {tests.length} tests
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {hasTests ? (
            <div className="space-y-3">
              {tests.slice(-10).reverse().map((test, index) => (
                <div key={index} className={`flex items-center justify-between p-4 border rounded-lg backdrop-blur-sm ${
                  test.success 
                    ? 'bg-green-50/60 border-green-200/60' 
                    : 'bg-red-50/60 border-red-200/60'
                }`}>
                  <div className="flex items-center gap-4">
                    <div className={`h-10 w-10 rounded-full flex items-center justify-center backdrop-blur-sm ${
                      test.success 
                        ? 'bg-green-100/80' 
                        : 'bg-red-100/80'
                    }`}>
                      {test.success ? (
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        Test {test.success ? 'Passed' : 'Failed'}
                      </p>
                      <p className="text-sm text-gray-600">
                        {new Date(test.timestamp).toLocaleString()}
                      </p>
                      {test.error && (
                        <p className="text-sm text-red-600 mt-1">{test.error}</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge variant={test.success ? 'secondary' : 'destructive'} className={
                      test.success 
                        ? 'bg-green-100 text-green-800 border-green-200' 
                        : ''
                    }>
                      {test.response?.status || 'No Response'}
                    </Badge>
                  </div>
                </div>
              ))}
              {tests.length > 10 && (
                <div className="text-center pt-4">
                  <p className="text-sm text-gray-500">
                    Showing last 10 tests of {tests.length} total
                  </p>
                  <Button variant="outline" size="sm" className="mt-2">
                    View All Tests
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <Clock className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No tests run yet</h3>
              <p className="text-gray-600 mb-4">Use the test runner above to execute your first test and see results here.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
